import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CATEGORIES, CONTACT } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_PRODUCTS = CATEGORIES.flatMap((c) => c.products.map((p) => p.name));

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product: params.get("product") || "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. Our team will reach out shortly.");
      setForm({ name: "", company: "", email: "", phone: "", product: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-4 text-base text-[#f5f5f5] placeholder:text-[#f5f5f5]/30 transition-colors duration-300";
  const labelCls = "font-mono-jb text-[10px] uppercase tracking-[0.25em] text-[#f5f5f5]/50";

  return (
    <main data-testid="contact-page" className="pt-[72px]">
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-20 sm:pt-32 pb-24 sm:pb-36">
        <p className="overline-label mb-8">Enquiries &amp; Quotes</p>
        <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[clamp(3rem,9vw,8rem)]" data-testid="contact-heading">
          <MaskedLine delay={0.1}>LET'S SUPPLY</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-[#D97736]">YOUR LINE.</span>
          </MaskedLine>
        </h1>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">
          <Reveal className="lg:col-span-3" delay={0.2}>
            <form onSubmit={submit} className="space-y-10" data-testid="enquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                <div>
                  <label htmlFor="enquiry-name" className={labelCls}>Name *</label>
                  <input id="enquiry-name" data-testid="enquiry-name-input" className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label htmlFor="enquiry-company" className={labelCls}>Company</label>
                  <input id="enquiry-company" data-testid="enquiry-company-input" className={inputCls} placeholder="Company / brand" value={form.company} onChange={set("company")} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className={labelCls}>Email *</label>
                  <input id="enquiry-email" type="email" data-testid="enquiry-email-input" className={inputCls} placeholder="you@company.com" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className={labelCls}>Phone</label>
                  <input id="enquiry-phone" data-testid="enquiry-phone-input" className={inputCls} placeholder="+91 ..." value={form.phone} onChange={set("phone")} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-product" className={labelCls}>Product of interest</label>
                  <select
                    id="enquiry-product"
                    data-testid="enquiry-product-select"
                    className={`${inputCls} bg-[#0a0a0a] appearance-none cursor-pointer`}
                    value={form.product}
                    onChange={set("product")}
                  >
                    <option value="">Select a product (optional)</option>
                    {ALL_PRODUCTS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className={labelCls}>Message *</label>
                  <textarea
                    id="enquiry-message"
                    data-testid="enquiry-message-input"
                    rows={4}
                    className={`${inputCls} resize-none`}
                    placeholder="Quantity, specification, destination port — anything that helps us quote faster."
                    value={form.message}
                    onChange={set("message")}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="group flex items-center gap-3 bg-[#D97736] text-[#0a0a0a] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-10 py-4 rounded-full transition-colors duration-300 hover:bg-[#e8904f] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? "Sending..." : "Send enquiry"}
                <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-2" delay={0.35}>
            <div className="border border-white/10 p-10 sm:p-12 space-y-12 lg:sticky lg:top-28" data-testid="contact-info-panel">
              <div>
                <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#f5f5f5]/40 mb-5">Direct</p>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-center gap-3 text-lg text-[#f5f5f5]/85 hover:text-[#D97736] transition-colors duration-300 mb-4">
                  <Phone className="w-5 h-5 text-[#D97736]" /> {CONTACT.phone}
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-3 text-lg text-[#f5f5f5]/85 hover:text-[#D97736] transition-colors duration-300">
                  <Mail className="w-5 h-5 text-[#D97736]" /> {CONTACT.email}
                </a>
              </div>
              <div>
                <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#f5f5f5]/40 mb-5">Head Office</p>
                <p className="flex gap-3 text-sm text-[#f5f5f5]/60 leading-relaxed" data-testid="contact-address">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-[#D97736]" />
                  <span>
                    <span className="text-[#f5f5f5]/85 font-medium">Head Office</span>
                    <br />{CONTACT.headOffice}
                  </span>
                </p>
              </div>
              <p className="font-mono-jb text-[10px] uppercase tracking-[0.25em] text-[#f5f5f5]/40 border-t border-white/10 pt-8">
                Typical response time: within 1 business day
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
